import JSON5 from "json5"

import type { Request } from "./har"

// https://github.com/ChromeDevTools/devtools-frontend/blob/66efa48538d7e2d9f9b77f53f750a44dcfab0856/front_end/panels/network/NetworkLogView.ts
export const enum FetchStyle {
  BROWSER = 0,
  NODE_JS = 1,
}

const { stringify } = JSON5

function extractURL(source: string) {
  const url = new URL(source)
  const search = url.searchParams

  if (!search.size || url.search.length < 20) {
    return JSON.stringify(source)
  }

  const prefix = url.origin + url.pathname + "?"
  const suffix = url.hash ? `+ ${JSON.stringify(url.hash)}` : ""

  const searchObject = Object.fromEntries(search)
  return `${JSON.stringify(prefix)} + new URLSearchParams(${stringify(searchObject, null, 2)}).toString()${suffix}`
}

export function generateFetchCall(request: Request, style: FetchStyle): string {
  const ignoredHeaders = new Set<string>([
    // Internal headers
    "method",
    "path",
    "scheme",
    "version",
    // Unsafe headers
    // Keep this list synchronized with src/net/http/http_util.cc
    "accept-charset",
    "accept-encoding",
    "access-control-request-headers",
    "access-control-request-method",
    "connection",
    "content-length",
    "cookie",
    "cookie2",
    "date",
    "dnt",
    "expect",
    "host",
    "keep-alive",
    "origin",
    "referer",
    "te",
    "trailer",
    "transfer-encoding",
    "upgrade",
    "via",
    // TODO(phistuck) - remove this once crbug.com/571722 is fixed.
    "user-agent",
  ])

  const credentialHeaders = new Set<string>(["cookie", "authorization"])

  const url = extractURL(request.url)

  const requestHeaders = request.headers
  const headerData: Headers = requestHeaders.reduce((result, header) => {
    const name = header.name

    if (!ignoredHeaders.has(name.toLowerCase()) && !name.includes(":")) {
      result.append(name, header.value)
    }

    return result
  }, new Headers())

  const headers: HeadersInit = {}
  for (const headerArray of headerData) {
    headers[headerArray[0]] = headerArray[1]
  }

  const credentials =
    request.cookies.length ||
    requestHeaders.some(({ name }) => credentialHeaders.has(name.toLowerCase()))
      ? "include"
      : "omit"

  const referrer = requestHeaders.find(
    ({ name }) => name.toLowerCase() === "referer"
  )?.value

  const requestBody = request.postData?.text

  const fetchOptions: RequestInit = {
    headers: Object.keys(headers).length ? headers : void 0,
    referrer,
    body: requestBody,
    method: request.method,
    mode: "cors",
  }

  if (style === FetchStyle.NODE_JS) {
    const cookieHeader = requestHeaders.find(
      header => header.name.toLowerCase() === "cookie"
    )
    const extraHeaders: HeadersInit = {}
    // According to https://www.npmjs.com/package/node-fetch#class-request the
    // following properties are not implemented in Node.js.
    delete fetchOptions.mode
    if (cookieHeader) {
      extraHeaders["cookie"] = cookieHeader.value
    }
    if (referrer) {
      delete fetchOptions.referrer
      extraHeaders["Referer"] = referrer
    }
    if (referrer) {
      delete fetchOptions.referrerPolicy
      extraHeaders["Referrer-Policy"] = referrer
    }
    if (Object.keys(extraHeaders).length) {
      fetchOptions.headers = {
        ...headers,
        ...extraHeaders,
      }
    }
  } else {
    fetchOptions.credentials = credentials
  }

  const options = stringify(fetchOptions, null, 2)
  return `fetch(${url}, ${options});`
}

export function generateAxiosCall(request: Request, style: FetchStyle): string {
  const ignoredHeaders = new Set<string>([
    // Internal headers
    "method",
    "path",
    "scheme",
    "version",
    // Unsafe headers
    // Keep this list synchronized with src/net/http/http_util.cc
    "accept-charset",
    "accept-encoding",
    "access-control-request-headers",
    "access-control-request-method",
    "connection",
    "content-length",
    "cookie",
    "cookie2",
    "date",
    "dnt",
    "expect",
    "host",
    "keep-alive",
    "origin",
    "referer",
    "te",
    "trailer",
    "transfer-encoding",
    "upgrade",
    "via",
    // TODO(phistuck) - remove this once crbug.com/571722 is fixed.
    "user-agent",
  ])

  const url = extractURL(request.url)

  const headers: Record<string, string> = {}
  for (const header of request.headers) {
    const name = header.name.toLowerCase()
    if (!ignoredHeaders.has(name) && !header.name.includes(":")) {
      headers[header.name] = header.value
    }
  }

  const referer = request.headers.find(
    ({ name }) => name.toLowerCase() === "referer"
  )?.value

  const requestBody = request.postData?.text

  const axiosConfig: Record<string, any> = {
    headers: Object.keys(headers).length ? headers : undefined,
    method: request.method.toLowerCase(),
  }

  if (referer) {
    axiosConfig.headers = {
      ...axiosConfig.headers,
      Referer: referer,
    }
  }

  if (style === FetchStyle.NODE_JS && referer) {
    axiosConfig.headers = {
      ...axiosConfig.headers,
      "Referrer-Policy": referer,
    }
  }

  delete axiosConfig.method

  const method = request.method.toLowerCase()
  const configStr = stringify(axiosConfig, null, 2)

  let body: string
  if (
    requestBody &&
    typeof axiosConfig.headers === "object" &&
    new Headers(axiosConfig.headers ?? {})
      .get("content-type")
      ?.includes("application/json")
  ) {
    try {
      const json = JSON.parse(requestBody)
      body = stringify(json, null, 2)
    } catch {
      body = stringify(requestBody)
    }
  } else {
    body = stringify(requestBody)
  }

  return ["get", "delete"].includes(method)
    ? `axios.${method}(${url}, ${configStr});`
    : `axios.${method}(${url}, ${body}, ${configStr});`
}
